import React, { useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  Select,
  Upload,
  Image,
  Spin,
  notification,
} from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";

import {
  _create_menu_raw_food,
  _update_menu,
  _upload_any_file,
} from "../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../common/utils/notification";
const { Option } = Select;

const RawFoodMenuForm = ({
  selectedMenu,
  categories,
  drawerVisibility,
  updateCount,
  setUpdateCount,
  user,
  navigate,
}) => {
  const [form] = Form.useForm();
  const [addingMenu, setAddingMenu] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [price, setPrice] = useState("");

  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );

  const handlePriceChange = (value) => {
    let numericValue = value.replace(/[^\d.]/g, "");
    setPrice(numericValue);
    form.setFieldsValue({ price: numericValue });
  };

  const openNotificationWithIcon = (type, title, message) => {
    notification[type]({
      message: title,
      description: message,
      placement: "bottom-right",
    });
  };

  const handlePriceBlur = () => {
    if (price) {
      const formattedPrice = parseFloat(price).toFixed(2);
      setPrice(formattedPrice);
      form.setFieldsValue({ price: formattedPrice });
    }
  };

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const changeImage = ({ fileList: newFileList }) => setFileList(newFileList);

  const handleRemoveImage = async (file) => {
    if (selectedMenu) {
      openNotificationWithIcon(
        "info",
        "Image removal not supported by backend",
        "Please update the menu to remove images."
      );
    }
  };

  const onFinish = async (values) => {
    try {
      setAddingMenu(true);
      let imageUrl = selectedMenu?.imageUrl || selectedMenu?.image_url || null;
      if (fileList.length > 0) {
        const fileToUpload = fileList[0].originFileObj;
        if (fileToUpload) {
          const response = await _upload_any_file(fileToUpload);
          imageUrl =
            response.data?.url ||
            response.data?.data?.url ||
            response.data?.path ||
            response.data?.data?.path ||
            null;
        } else if (fileList[0].url) {
          imageUrl = fileList[0].url;
        } else if (fileList[0].thumbUrl) {
          imageUrl = fileList[0].thumbUrl;
        }
      }

      const data = {
        name: values.itemName,
        description: values.description || "",
        price: parseFloat(values.price),
        minUnits: selectedMenu?.minUnits || 5,
        categoryId: values.category,
        openingStock: values.opening_stock || 0,
        imageUrl,
        capacity: parseFloat(values.capacity),
        unit: values.unit,
      };

      let response;
      if (selectedMenu) {
        response = await _update_menu(selectedMenu.id, data);
        openNotificationWithIcon(
          "success",
          response?.data?.title,
          response?.data?.message
        );
      } else {
        response = await _create_menu_raw_food(data);
        showSuccessNotification(
          response?.data?.message || "Menu created successfully"
        );
      }
      form.resetFields();
      drawerVisibility();
      setFileList([]);
      setUpdateCount(updateCount + 1);
    } catch (err) {
      if (err.response) {
        if (err.response.data.message === "Unauthorized") {
          localStorage.removeItem("konnect_token");
          navigate("/signin");
        }
        showErrorNotification(err.response.data.message);
      } else {
        openNotificationWithIcon("error", err.message);
      }
    } finally {
      setAddingMenu(false);
    }
  };

  React.useEffect(() => {
    if (selectedMenu) {
      if (selectedMenu.imageUrl) {
        const mappedFileList = [
          {
            uid: "1",
            name: "image",
            status: "done",
            thumbUrl: selectedMenu.imageUrl.split("?")[0],
            url: selectedMenu.imageUrl.split("?")[0],
          },
        ];
        setFileList(mappedFileList);
      } else {
        setFileList([]);
      }

      form.setFieldsValue({
        itemName: selectedMenu.name,
        price: selectedMenu.price,
        category: selectedMenu.category?.id,
        description: selectedMenu.description,
        opening_stock: selectedMenu.openingStock,
        unit: selectedMenu.unit,
        capacity: selectedMenu.capacity,
      });
    }
  }, [selectedMenu, form]);

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Form.Item
        label="Upload Item Images"
        rules={[{ required: true, message: "Kindly add the item image" }]}
      >
        <Upload
          action="https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload"
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={changeImage}
          onRemove={(file) => handleRemoveImage(file)}
        >
          {fileList.length >= 1 ? null : uploadButton}
        </Upload>
        {previewImage && (
          <Image
            wrapperStyle={{ display: "none" }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(""),
            }}
            src={previewImage}
          />
        )}
      </Form.Item>

      <Form.Item
        name="category"
        label="Select Category"
        rules={[{ required: true, message: "Please select a category!" }]}
      >
        <Select placeholder="Select category">
          {categories.map((category) => (
            <Option key={category.id} value={category.id}>
              {category.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label="Item Name"
        name="itemName"
        rules={[{ required: true, message: "Please fill input is required" }]}
      >
        <Input placeholder="Enter item name" />
      </Form.Item>

      <Form.Item
        label="Capacity"
        name="capacity"
        rules={[{ required: true, message: "Please enter capacity" }]}
      >
        <InputNumber
          min={0}
          className="w-full"
          type="number"
          inputMode="numeric"
          placeholder="Enter capacity (kg or litres)"
        />
      </Form.Item>

      <Form.Item
        label="Unit"
        name="unit"
        rules={[{ required: true, message: "Please select a unit" }]}
      >
        <Select placeholder="Select unit">
          <Option value="Kg">Kg</Option>
          <Option value="Litres">Litres</Option>
          {/* <Option value="Number">Number</Option> */}
        </Select>
      </Form.Item>

      <Form.Item
        label="Opening Stock"
        name="opening_stock"
        rules={[{ required: true, message: "Please fill input is required" }]}
      >
        <InputNumber
          min={0}
          className="w-full"
          type="number"
          inputMode="numeric"
          placeholder="Enter extra opening stock"
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="Wholesale Price"
            name="price"
            rules={[
              { required: true, message: "Please enter wholesale price" },
            ]}
          >
            <Input
              prefix="NGN"
              value={price}
              onChange={(e) => handlePriceChange(e.target.value)}
              onBlur={handlePriceBlur}
              type="number"
              inputMode="numeric"
              placeholder="Enter wholesale price"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Description" name="description">
        <Input.TextArea placeholder="Description" rows={3} />
      </Form.Item>

      <div className={"flex justify-between"}>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={drawerVisibility}
            className="menu-update-btn bg-gray-500 text-white hover:bg-gray-600 border-none px-6 py-3 rounded-xl tracking-wider transition-colors"
          >
            Back
          </button>
          {addingMenu ? (
            <button
              className="menu-update-btn bg-gray-300 hover:bg-gray-300 hover:text-gray-500 text-gray-500 cursor-not-allowed border-none px-10 py-3 rounded-xl flex items-center justify-center tracking-wider transition-colors"
              disabled
            >
              {selectedMenu ? "Editing Item" : "Adding Item"}{" "}
              <Spin style={{ marginLeft: "10px" }} indicator={newAntIcon} />{" "}
            </button>
          ) : (
            <button
              type="submit"
              className="menu-update-btn bg-gray-900 text-white hover:bg-gray-800 border-none px-10 py-3 rounded-xl tracking-wider transition-colors"
            >
              {selectedMenu ? "Edit Item" : "Add Item"}
            </button>
          )}
        </div>
      </div>
    </Form>
  );
};

export default RawFoodMenuForm;
